/* eslint-disable complexity */
/*
 * Stores a list of space member ids, organized by role. This data is used for the users page.
 *
 * Each id refers to an entry in `entities.spaceMembers` or `entities.invitations`
 * {
 *     <spaceID>: {
 *        admin: [123, 432, 232],
 *        member: [968, 585, 606],
 *        limited_member: [243, 453, 534],
 *        invitations: [533, 522, 2452],
 *     }
 * }
 */

import _uniq from "lodash/uniq";
import * as actionTypes from "../actions/actionTypes";

export default function spaceMembers(state = {}, action) {
  switch (action.type) {
    case actionTypes.FETCH_SPACE_MEMBERS:
    case actionTypes.FETCH_SPACE_INVITATIONS: {
      const { spaceId, page, sort, role } = action;
      const members = state[spaceId],
        pagination = { page, sort, isFetching: true },
        roleData = page === 0 ? { data: [] } : members[role];

      return {
        ...state,
        [spaceId]: { ...members, [role]: { ...roleData, ...pagination } },
      };
    }
    case actionTypes.FETCH_SPACE_MEMBERS_SUCCESS:
    case actionTypes.FETCH_SPACE_INVITATIONS_SUCCESS: {
      const {
        spaceId,
        page,
        role,
        response: {
          result: {
            offset,
            limit,
            total,
            sort,
            members: newMembers = [],
            invitations: newInvitations = [],
          },
        },
      } = action;
      const members = state[spaceId],
        hasMorePages = offset + limit < total,
        existingMembers = members[role]?.data ?? [],
        totalMembers =
          page === 0
            ? [...newMembers, ...newInvitations]
            : [...existingMembers, ...newMembers, ...newInvitations];
      return {
        ...state,
        [spaceId]: {
          ...members,
          [role]: {
            sort,
            page,
            isFetching: false,
            hasMorePages,
            data: totalMembers,
          },
        },
      };
    }
    case actionTypes.FETCH_SPACE_MEMBER_COUNTS_SUCCESS: {
      const {
        spaceId,
        response: { member_counts: counts },
      } = action;
      return { ...state, [spaceId]: { ...state[spaceId], counts } };
    }
    case actionTypes.INVITE_MEMBERS_SUCCESS: {
      const {
          spaceId,
          response: { result },
        } = action,
        stateSpace = state[spaceId];
      let { counts, invite } = stateSpace;

      const existingInvites = new Set(invite?.data || []);
      // Filter out failed invites where result is null
      const newInvites = result.invitations
        .map(res => res.invitation)
        .filter(id => id && !existingInvites.has(id));

      if (invite) {
        invite = { ...invite, data: [...invite.data, ...newInvites] };
      }
      if (counts) {
        counts = { ...counts, invitations: counts.invitations + newInvites.length };
      }
      return { ...state, [spaceId]: { ...stateSpace, counts, invite } };
    }
    case actionTypes.DELETE_INVITATION_SUCCESS: {
      const { spaceId, invitationIds } = action,
        stateSpace = state[spaceId];
      let { counts, invite } = stateSpace;

      if (invite) {
        invite = { ...invite, data: invite.data.filter(id => !invitationIds.includes(id)) };
      }
      if (counts) {
        counts = { ...counts, invitations: counts.invitations - invitationIds.length };
      }
      // return state;
      return { ...state, [spaceId]: { ...stateSpace, counts, invite } };
    }
    case actionTypes.CHANGE_SPACE_MEMBER_ROLE_SUCCESS: {
      const { spaceId, role, prevRole } = action,
        spaceState = state[spaceId],
        roleCount =
          spaceState.counts?.[role] !== undefined ? spaceState.counts?.[role] + 1 : undefined,
        prevRoleCount =
          spaceState?.counts?.[prevRole] !== undefined
            ? spaceState.counts?.[prevRole] - 1
            : undefined,
        counts = { ...spaceState.counts, [role]: roleCount, [prevRole]: prevRoleCount };
      return {
        ...state,
        [spaceId]: { ...spaceState, counts, all: {}, [role]: {}, [prevRole]: {} },
      };
    }
    case actionTypes.BATCH_CHANGE_SPACE_MEMBER_ROLE_SUCCESS: {
      const {
          spaceId,
          role,
          prevRoles,
          response: {
            result: { members },
          },
        } = action,
        spaceState = state[spaceId],
        counts = { ...spaceState.counts };

      members.forEach(({ member: memId, success }) => {
        if (!success) return;

        const prevRole = prevRoles[memId];
        if (counts[prevRole] !== undefined) counts[prevRole] -= 1;
        if (counts[role] !== undefined) counts[role] += 1;
      });
      const wipeLists = _uniq(Object.values(prevRoles)).reduce(
        (acc, r) => {
          acc[r] = {};
          return acc;
        },
        { all: {}, [role]: {} }
      );
      return {
        ...state,
        [spaceId]: { ...spaceState, counts, ...wipeLists },
      };
    }
    case actionTypes.DELETE_SPACE_MEMBERS_SUCCESS: {
      const {
          spaceId,
          response: {
            result,
            entities: { spaceMembers: removedMembers },
          },
        } = action,
        spaceState = state[spaceId];

      const counts = { ...spaceState.counts };
      const reset = { all: {} };

      result.members.forEach(({ member: memId, success }) => {
        if (!success) return;
        const role = removedMembers[memId].role;
        counts[role] -= 1;
        reset[role] = {};
      });

      return { ...state, [spaceId]: { ...spaceState, counts, ...reset } };
    }
    default:
      return state;
  }
}
